// Rapido-modelldata som driver /rapido/modeller/$model.
// Specifikationer är typiska värden för modellserien — exakta mått varierar
// per årsmodell och chassi. Vi anger intervall där det spelar roll.

export type RapidoModel = {
  slug: string;
  name: string;
  h1: string;
  category: "Halvintegrerad husbil" | "Integrerad husbil" | "Plåthusbil (van)" | "Alkovhusbil";
  chassis: string;
  intro: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  totalWeightKg: string;
  sleeps: string;
  belts: string;
  beds: string;
  kitchen: string;
  bathroom: string;
  heating: string;
  fresh: string;
  waste: string;
  battery: string;
  garage: string;
  perfectFor: string[];
  strengths: string[];
  weaknesses: string[];
  priceUsed: string;
  priceNew: string;
  keyword: string;
  metaTitle: string;
  metaDesc: string;
  faq: { q: string; a: string }[];
};

export const RAPIDO_MODELS: RapidoModel[] = [
  {
    slug: "v55",
    name: "Rapido V55",
    h1: "Rapido V55",
    category: "Plåthusbil (van)",
    chassis: "Fiat Ducato eller Citroën Jumper, oftast 140–160 hk",
    intro:
      "V55 är Rapidos kompaktaste plåthusbil — under 5,5 meter och parkerbar som en vanlig skåpbil. Byggd för par som vill kunna ta bilen överallt utan att kompromissa med bad, kök eller en riktig säng.",
    lengthCm: "541",
    widthCm: "205",
    heightCm: "258",
    totalWeightKg: "3 500 (B-körkort)",
    sleeps: "2 vuxna (+ eventuell taksäng som tillval)",
    belts: "4 bältade platser",
    beds: "Fransk säng bak, ca 190 × 140 cm",
    kitchen: "Kokplatta 2-låga, kompressorkyl 90 l, diskho i rostfritt",
    bathroom: "Kompakt våtrum med kassetttoalett och separat dusch",
    heating: "Truma Combi 4 (gas + varmvatten)",
    fresh: "100 l färskvatten",
    waste: "90 l gråvatten",
    battery: "95 Ah AGM standard, litium som tillval",
    garage: "Litet lastutrymme under bakre säng — plats för ett par cyklar/campingstolar",
    perfectFor: [
      "Par som reser mycket och vill kunna parkera i stan",
      "Förstagångsköpare som vill hålla ner storleken",
      "Wildcamping och norra Skandinavien där skogsvägar är smala",
    ],
    strengths: [
      "Kort — kommer överallt en vanlig van kommer",
      "Låg totalvikt — går på vanligt B-körkort",
      "Låg bränsleförbrukning, oftast 0,9–1,1 l/mil",
    ],
    weaknesses: [
      "Trång att bo i över flera veckor på hösten/vintern",
      "Litet garage — glöm elcyklar och stora grillar",
    ],
    priceUsed: "Ca 550 000 – 850 000 kr beroende på år och utrustning",
    priceNew: "Från ca 1 050 000 kr (2024–2025)",
    keyword: "rapido v55",
    metaTitle: "Rapido V55 – Specifikationer, pris & vad du bör veta | AK Husbil",
    metaDesc:
      "Rapido V55 kompakt plåthusbil under 5,5 m: mått, vikt, sängar, pris och våra erfarenheter från verkstaden. Officiell Rapido-återförsäljare i Kalix.",
    faq: [
      {
        q: "Får jag köra Rapido V55 på B-körkort?",
        a: "Ja. V55 har totalvikt 3 500 kg vilket ligger inom B-körkortets gräns. Var uppmärksam på lastvikten — full vattentank, gasolflaska, cyklar och personlig utrustning drar snabbt iväg. Vi hjälper dig räkna igenom lastutrymmet innan du köper.",
      },
      {
        q: "Hur lång är egentligen en Rapido V55?",
        a: "Ca 5,41 meter lång, 2,05 m bred och 2,58 m hög. Det gör den kortare än många familjebilar med släp — du parkerar den på en vanlig långparkering.",
      },
      {
        q: "Är V55 för trång för två personer i en månad?",
        a: "Många kunder gör det, men det kräver planering. Sittgruppen blir mat- och arbetsplats, utrymmet under sängen används smart, och du vänjer dig vid att packa upp/ihop dagligen. På långresor väljer många ändå V62 eller V68 för en extra meter innerlängd.",
      },
      {
        q: "Vad ligger bränsleförbrukningen på?",
        a: "Realistiskt 0,9–1,2 l/mil i landsvägsfart med 140 hk-motorn. Motorväg i 110 km/h ökar snabbt till 1,2–1,3. Med litiumbatteri och elcyklar blir vikten högre och förbrukningen med.",
      },
    ],
  },
  {
    slug: "v62",
    name: "Rapido V62",
    h1: "Rapido V62",
    category: "Plåthusbil (van)",
    chassis: "Fiat Ducato, oftast 140–180 hk",
    intro:
      "V62 är Rapidos populäraste plåthusbil för par. 6,4 meter ger plats för fransk säng bak, riktigt kök och komfortabelt våtrum — utan att bli så stor att den blir jobbig att parkera.",
    lengthCm: "640",
    widthCm: "205",
    heightCm: "265",
    totalWeightKg: "3 500 (B-körkort)",
    sleeps: "2 vuxna (+ taksäng som tillval)",
    belts: "4 bältade platser",
    beds: "Fransk säng bak, ca 195 × 150 cm",
    kitchen: "Kokplatta 3-låga, kompressorkyl 90–133 l, ordentlig diskbänk",
    bathroom: "Våtrum med svängbar vägg — separat dusch när du behöver",
    heating: "Truma Combi 4 gas eller Diesel Combi 6 D (populärt uppgraderingsval)",
    fresh: "100–120 l färskvatten",
    waste: "90–100 l gråvatten",
    battery: "95 Ah AGM standard, 100–200 Ah litium som tillval",
    garage: "Genomlastning under bakre säng — cyklar, campingstolar, mindre grill",
    perfectFor: [
      "Par som ska resa 2–6 månader per år",
      "Bytesköpare som vill upp från äldre halvintegrerad",
      "Norra Sverige/Norge där dieselvärme är prioritet",
    ],
    strengths: [
      "Bästa kompromissen storlek/komfort i Rapidos plåthusbilar",
      "Genomtänkt planlösning — våtrum känns dubbelt så stort som det är",
      "Håller värdet extremt bra på begagnatmarknaden",
    ],
    weaknesses: [
      "Fransk säng är inte perfekt om ni går upp i olika tider",
      "Standardbatteri räcker inte för dig som frostcampar",
    ],
    priceUsed: "Ca 650 000 – 950 000 kr beroende på år och utrustning",
    priceNew: "Från ca 1 150 000 kr (2024–2025)",
    keyword: "rapido v62",
    metaTitle: "Rapido V62 – Mått, pris & planlösning | AK Husbil & Fritid",
    metaDesc:
      "Rapido V62 på 6,4 m: specifikationer, mått, vikt, pris begagnad och ny. Våra erfarenheter från verkstaden och vad du bör kolla innan köp.",
    faq: [
      {
        q: "Vad är skillnaden mellan Rapido V62 och V68?",
        a: "V62 har fransk säng — en dubbelsäng där ni sover ihop. V68 har längsgående enkelsängar bak. Bägge är 6,3–6,4 m långa. Enkelsängar är populärare för par som går upp i olika tider eller där någon är lättstörd; fransk säng ger mer förvaring bak.",
      },
      {
        q: "Klarar V62 vinterresor i Norrland?",
        a: "Ja, särskilt med Truma Combi 6 D (diesel). Vi rekommenderar isolerade avloppsslangar, dubbla golv där det finns och en extra AGM eller litium om du står långa nätter utan el. Vi går igenom vinterpaketen med dig.",
      },
      {
        q: "Är V62 för stor för första husbilen?",
        a: "Inte i praktiken — bredden är samma som V55 (2,05 m). Extra längden märks på parkering men inte i trafik. Många förstagångsköpare landar just på V62 för att den håller värdet så bra vid en eventuell bytesaffär.",
      },
      {
        q: "Vad kostar en begagnad Rapido V62?",
        a: "Ca 650 000–950 000 kr beroende på år, mil och utrustning. En välskött 2020–2022 med litium, dieselvärme och solpaneler ligger ofta i övre halvan. Vi har alltid några i lager — kolla senaste flödet på fordonsidan.",
      },
    ],
  },
  {
    slug: "v68",
    name: "Rapido V68",
    h1: "Rapido V68",
    category: "Plåthusbil (van)",
    chassis: "Fiat Ducato, 140–180 hk",
    intro:
      "V68 är parhusbilen där ni sover i varsin säng bak — utan att förlora sittgrupp, kök eller riktigt våtrum. En av Rapidos mest omtyckta modeller för långresor.",
    lengthCm: "636",
    widthCm: "205",
    heightCm: "265",
    totalWeightKg: "3 500 (B-körkort)",
    sleeps: "2 vuxna",
    belts: "4 bältade platser",
    beds: "Två längsgående enkelsängar bak, ca 195 × 80 cm var",
    kitchen: "Kokplatta 3-låga, kompressorkyl 90–133 l",
    bathroom: "Våtrum med svängbar vägg och separat dusch",
    heating: "Truma Combi 4 eller 6 D (diesel)",
    fresh: "100–120 l färskvatten",
    waste: "90–100 l gråvatten",
    battery: "95 Ah AGM standard, litium som tillval",
    garage: "Lastutrymme mellan sängarna — plats för cyklar (även elcyklar) och stolar",
    perfectFor: [
      "Par där någon är lättstörd/går upp tidigt",
      "Långresenärer som vill ha eget utrymme",
      "Kunder som vill ha maximal lastkapacitet i garaget",
    ],
    strengths: [
      "Enkelsängar = bättre sömn efter en lång körning",
      "Större effektivt garage än V62",
      "Går fint att koppla ihop sängarna till stor liggyta",
    ],
    weaknesses: [
      "Mindre förvaring bak jämfört med fransk säng",
      "Sängarna är 80 cm breda — trångt för storvuxna",
    ],
    priceUsed: "Ca 700 000 – 980 000 kr",
    priceNew: "Från ca 1 200 000 kr (2024–2025)",
    keyword: "rapido v68",
    metaTitle: "Rapido V68 – Enkelsängar, mått & pris | AK Husbil & Fritid",
    metaDesc:
      "Rapido V68 på 6,36 m med två längsgående enkelsängar. Specs, mått, vikt, pris och verkstadens erfarenheter. Officiell Rapido-återförsäljare.",
    faq: [
      {
        q: "Hur breda är sängarna i Rapido V68?",
        a: "Vanligtvis ca 195 × 80 cm styck. Det är komfortabelt för de flesta men trångt om båda är över 185 cm eller kraftigt byggda — då är V62 med fransk säng ofta bekvämare.",
      },
      {
        q: "Går det att koppla ihop sängarna i V68?",
        a: "Ja. Rapido levererar en mellandyna som tillval — sängarna blir en stor liggyta för mysstunder eller om barnbarn följer med. Vardagsfördelen med separata sängar är kvar.",
      },
      {
        q: "Får plats med två elcyklar i garaget?",
        a: "Vanligtvis ja, med rätt hållare. Lastutrymmet mellan sängarna är större än på V62 eftersom fransk säng-lådan inte tar plats. Vi visar dig hur du lastar rätt — särskilt viktigt för viktfördelningen.",
      },
      {
        q: "Vad kostar en Rapido V68 begagnad?",
        a: "Ca 700 000–980 000 kr. Årsmodeller 2021+ med litiumbatteri och dieselvärme ligger normalt över 850 000. Vi går igenom både servicehistorik och skick innan vi sätter pris.",
      },
    ],
  },
  {
    slug: "866f",
    name: "Rapido 866F",
    h1: "Rapido 866F",
    category: "Halvintegrerad husbil",
    chassis: "Fiat Ducato AL-KO, oftast 150–180 hk",
    intro:
      "866F är en klassisk halvintegrerad Rapido — franskt byggkvalitet, dubbla golv och en av branschens tystaste konstruktioner. Rätt val för dig som vill ha riktig husbilskänsla, inte van.",
    lengthCm: "749",
    widthCm: "232",
    heightCm: "289",
    totalWeightKg: "3 500 (uppgraderbar till 4 250)",
    sleeps: "2 vuxna + 2 i taksäng",
    belts: "4 bältade platser",
    beds: "Fransk säng bak + elektrisk taksäng över sittgruppen",
    kitchen: "Kokplatta 3-låga, kylskåp 138–142 l, ordentlig arbetsyta",
    bathroom: "Rundat våtrum, separat dusch",
    heating: "Truma Combi 6 (gas) eller Alde vattenburen på senare årsmodeller",
    fresh: "120 l färskvatten",
    waste: "90 l gråvatten",
    battery: "95 Ah AGM standard, litium tillval",
    garage: "Stort bakgarage — plats för elcyklar, bord, stolar och mer",
    perfectFor: [
      "Par eller familj som vill åka längre perioder",
      "Bytesköpare från van som vill ha mer plats och komfort",
      "Vinterresenärer — dubbla golv gör stor skillnad",
    ],
    strengths: [
      "Dubbla golv → varmt även i minusgrader",
      "Alkovlöst tak → bättre bränsleekonomi än alkov",
      "Extremt god ljudisolering på motorväg",
    ],
    weaknesses: [
      "Kräver större parkeringsyta än plåthusbilarna",
      "Bredden 2,32 m gör smala vägar mer intensiva",
    ],
    priceUsed: "Ca 750 000 – 1 250 000 kr beroende på år",
    priceNew: "Från ca 1 550 000 kr (2024–2025)",
    keyword: "rapido 866f",
    metaTitle: "Rapido 866F – Halvintegrerad specifikationer & pris | AK Husbil",
    metaDesc:
      "Rapido 866F med dubbla golv och fransk säng: mått, vikt, planlösning, pris och våra erfarenheter från verkstaden i Kalix.",
    faq: [
      {
        q: "Är Rapido 866F byggd för vinterbruk?",
        a: "Ja, det är en av de starkaste anledningarna att välja den. Dubbla golv innebär att vatten- och avloppstank ligger uppvärmda inuti bilen. Med Truma Combi 6 eller Alde värmer den ordentligt även i -20 °C, förutsatt att du har rätt bränslemängd (gas eller diesel).",
      },
      {
        q: "Vad väger 866F och behöver jag C-körkort?",
        a: "Standardtotalvikt är 3 500 kg — B-körkort räcker. Många kunder uppgraderar till 4 250 kg totalvikt för att få mer lastmarginal; då krävs B96 eller C1 beroende på om du drar släp.",
      },
      {
        q: "Vilka vanliga fel ska jag kolla på en begagnad 866F?",
        a: "Vi kollar alltid: fuktvärden i vägg och tak, taksängens elmotor, kylskåpets tredje bränslekälla (gasol/12 V/230 V), och att bromsservon på AL-KO-chassit inte har fått rost. Servicehistoriken avgör mycket för värdet.",
      },
      {
        q: "Passar 866F som familjebil?",
        a: "Ja, för familj med 2 barn i låg-/mellanstadieålder — den elektriska taksängen är riktigt smart. Tonårsfamilj vill oftare ha en modell med bäddbar sittgrupp bak.",
      },
    ],
  },
  {
    slug: "9005-dfh",
    name: "Rapido 9005 DFH",
    h1: "Rapido 9005 DFH",
    category: "Integrerad husbil",
    chassis: "Fiat Ducato AL-KO, oftast 180 hk",
    intro:
      "9005 DFH är Rapidos flaggskeppsintegrerade med drottningsäng bak, riktigt bakgarage och en känsla som ligger närmare ett husvagnshotell än en husbil. För dig som vill bo, inte campa.",
    lengthCm: "898",
    widthCm: "232",
    heightCm: "297",
    totalWeightKg: "4 500 (C1-körkort krävs)",
    sleeps: "2 vuxna + 2 i eldriven taksäng",
    belts: "4 bältade platser",
    beds: "Drottningsäng 200 × 150 cm bak + eldriven fällbar taksäng",
    kitchen: "Kokplatta 3-låga, kompressorkyl 175 l med separat frys, granitliknande bänkskiva",
    bathroom: "Separat toarum och dusch — riktig helhet",
    heating: "Alde vattenburen värme (standard eller nära det)",
    fresh: "140 l färskvatten",
    waste: "100 l gråvatten",
    battery: "2 × 95 Ah AGM standard, litium 200 Ah som tillval",
    garage: "Stort bakgarage — plats för scooter, elcyklar, grill och campingmöbler",
    perfectFor: [
      "Par som bor i husbilen 6–12 månader per år",
      "Digitala nomader — kontorsplats i sittgruppen fungerar dagligen",
      "Kunder som vill ha maximal isolering och tystnad",
    ],
    strengths: [
      "Integrerat förarparti = varmare, tystare, bättre panoramaruta",
      "Alde-värme = torr, jämn värme utan gasförbrukning",
      "Bakgaraget är ett av branschens bästa",
    ],
    weaknesses: [
      "Kräver C1 och parkeringsplan — 9 m är 9 m",
      "Investering — men värdeminskningen är ovanligt låg",
    ],
    priceUsed: "Ca 1 300 000 – 2 100 000 kr beroende på år",
    priceNew: "Från ca 2 350 000 kr (2024–2025)",
    keyword: "rapido 9005 dfh",
    metaTitle: "Rapido 9005 DFH – Integrerad flaggskepp | AK Husbil & Fritid",
    metaDesc:
      "Rapido 9005 DFH: integrerad husbil med drottningsäng, Alde-värme och stort garage. Specifikationer, pris, körkort och våra kommentarer.",
    faq: [
      {
        q: "Behöver jag C1-körkort för Rapido 9005 DFH?",
        a: "Ja. Totalvikten ligger på 4 500 kg vilket kräver B96 (för lätt släp) eller C1 för själva husbilen. Vi hjälper dig kolla vad ditt körkort ger dig innan du beställer.",
      },
      {
        q: "Är Alde-värmen värd merkostnaden?",
        a: "För en integrerad husbil som ska köras året runt: absolut ja. Alde ger jämn, torr värme utan gasförbrukning och håller kylan borta även vid långa kalla nätter. Vi ser sällan reklamationer på Alde när servicehistoriken hålls.",
      },
      {
        q: "Hur mycket får plats i bakgaraget?",
        a: "På 9005 DFH: 2 elcyklar, en gasolgrill, campingmöbler och en scooter — med rätt lastningsplan. Vi visar viktfördelning och hur du undviker att baklastad bil blir instabil.",
      },
      {
        q: "Vad blir bränsleförbrukningen?",
        a: "Realistiskt 1,3–1,6 l/mil i landsvägsfart med 180 hk-motorn. Motorväg i 110 km/h kan ge 1,7. Vikt och profil styr mycket — men det är fortfarande under vad många fyrhjulsdrivna SUV:ar drar med släp.",
      },
    ],
  },
  {
    slug: "platis",
    name: "Rapido Plåtis",
    h1: "Rapido plåtis (V-serien)",
    category: "Plåthusbil (van)",
    chassis: "Fiat Ducato eller Citroën Jumper, 140–180 hk",
    intro:
      "'Plåtis' är svenskarnas smeknamn på Rapidos plåthusbilar — V-serien. Byggda som en förlängd skåpbil men inredda med franskt precisionshantverk. Nedan hittar du alla modellerna vi hjälper till med.",
    lengthCm: "541–668",
    widthCm: "205",
    heightCm: "258–265",
    totalWeightKg: "3 500 (B-körkort)",
    sleeps: "2 vuxna",
    belts: "4 bältade platser",
    beds: "Fransk säng eller enkelsängar bak, beroende på modell",
    kitchen: "Kokplatta 2–3-låga, kompressorkyl 90–133 l",
    bathroom: "Kompakt våtrum med toa och dusch",
    heating: "Truma Combi 4 gas eller 6 D diesel",
    fresh: "100–120 l",
    waste: "90–100 l",
    battery: "95 Ah AGM standard, litium tillval",
    garage: "Litet bakre lastutrymme — varierar per modell",
    perfectFor: [
      "Par som vill komma överallt en van kommer",
      "Kunder som prioriterar bränsleekonomi och parkering",
      "Wildcamping i norr, Norge, brittiska öar",
    ],
    strengths: [
      "Under 6,7 m — parkerar som en vanlig bil",
      "B-körkort räcker — 3 500 kg totalvikt",
      "Franskt inredningshantverk i van-format",
    ],
    weaknesses: [
      "Trängre än halvintegrerad över långa perioder",
      "Litet garage — planera lasten noga",
    ],
    priceUsed: "Ca 550 000 – 980 000 kr beroende på modell och år",
    priceNew: "Från ca 1 050 000 kr (V55) till ca 1 250 000 (V68) 2024–2025",
    keyword: "rapido plåtis",
    metaTitle: "Rapido plåtis – V55, V62, V65, V68 översikt | AK Husbil & Fritid",
    metaDesc:
      "Rapidos plåthusbilar (V-serien) — mått, vikter, priser och skillnader mellan V55, V62, V65 och V68. Officiell Rapido-återförsäljare i Kalix.",
    faq: [
      {
        q: "Vad menas med 'plåtis' egentligen?",
        a: "Plåthusbil — en husbil byggd på en förlängd skåpbils originalkaross istället för ett separat husbilsaggregat. Rapidos V-serie (V55, V62, V65, V68 m.fl.) är alla plåtishusbilar.",
      },
      {
        q: "Vilken Rapido plåtis passar mig?",
        a: "V55 om du prioriterar smidighet och stad. V62 om du vill ha fransk säng och långresekomfort. V68 om ni vill ha varsin säng. V65 är mellanstor kompromiss. Vi går igenom skillnaderna på plats i Kalix.",
      },
      {
        q: "Är plåthusbil sämre isolerad än halvintegrerad?",
        a: "Historiskt ja, men Rapidos senaste V-serie är ovanligt bra på isolering — de har jobbat mycket med köldbryggor. För vinterresor i Norrland rekommenderar vi ändå dieselvärme (Truma Combi 6 D) och extra madrassisolering.",
      },
      {
        q: "Vad kostar en begagnad Rapido plåtis?",
        a: "Ca 550 000–980 000 kr beroende på modell, årsmodell och utrustning. Litium och dieselvärme lyfter värdet 50 000–100 000 kr. Vi har alltid några i lager — kolla vårt fordonsflöde.",
      },
    ],
  },
];

export function getRapidoModel(slug: string): RapidoModel | undefined {
  return RAPIDO_MODELS.find((m) => m.slug === slug);
}

export const RAPIDO_MODEL_SLUGS = RAPIDO_MODELS.map((m) => m.slug);
